import { cn } from '@/lib/utils';

export type PlacaCcSize = 'sm' | 'lg';

interface Props {
  cc: string;
  /** Color de fondo del cliente (clientes.color) — sin él, placa neutra (--plate). */
  color?: string | null;
  /** Color del texto sobre `color` (contraste, clientes.color_texto). */
  colorTexto?: string | null;
  /** 'sm' (default, 15px/rounded-7 — obra-card.tsx, estado-redes-dialog.tsx)
   *  · 'lg' (17px/rounded-8 — header-obra.tsx, el header de DetalleObra). */
  size?: PlacaCcSize;
  className?: string;
}

const SIZE_CLASSES: Record<PlacaCcSize, string> = {
  sm: 'rounded-[7px] px-2 py-1 text-[15px]',
  lg: 'rounded-[8px] px-[9px] py-[5px] text-[17px]',
};

// Placa del CC (mono, --plate, sufijo UTE chico, color de cliente con borde
// rgba(0,0,0,.14) y sufijo con opacity .72) — receta única de obra-card.tsx,
// extraída para dejar de duplicarla en cada consumidor (Avivador §10.4).
//
// `size` cubre también las dos variantes de "borde en dark" que ya existían
// antes de esta extracción, sin agregar una prop nueva: la neutra 'sm'
// (obra-card, estado-redes-dialog) llevaba `dark:border-white/[0.09]`; la
// 'lg' (header-obra) no la llevaba — se preserva tal cual para no cambiar el
// look de header-obra. `group-hover:bg-plate-hover` va siempre en la
// variante neutra: solo tiene efecto real dentro de un ancestro con clase
// `group` (la card lo es; el header y el diálogo no), así que incluirlo acá
// no cambia nada donde antes no estaba.
export function PlacaCc({ cc, color, colorTexto, size = 'sm', className }: Props) {
  const match = cc.match(/^(\S+)\s*(.*)$/);
  const main = match ? match[1] : cc;
  const suf = match ? match[2] : '';

  return (
    <span
      className={cn(
        'flex-none border font-mono font-bold leading-none',
        SIZE_CLASSES[size],
        color
          ? ''
          : cn(
              'border-transparent bg-plate text-foreground group-hover:bg-plate-hover',
              size === 'sm' && 'dark:border-white/[0.09]',
            ),
        className,
      )}
      style={color ? { background: color, color: colorTexto ?? undefined, borderColor: 'rgba(0,0,0,0.14)' } : undefined}
    >
      {main}
      {suf && (
        <span
          className={cn('ml-1 text-[10px] font-semibold tracking-[0.05em]', color ? 'opacity-[0.72]' : 'text-muted-foreground')}
        >
          {suf}
        </span>
      )}
    </span>
  );
}
